// Offline cache for bell sound files served from R2 storage.
//
// Requirement: a bell's audio file is loaded from the backend (an R2 storage
// URL) as the first priority. Only when that URL is null / empty / unreachable
// do we fall back to a bundled sound in /public/bells. To make the R2 file
// reliable (even when the device briefly loses network at the exact moment a
// bell must ring), we pre-fetch every bell's R2 file and cache the blob locally
// on each login and on every bell save/update.
//
// Audio blobs are far too large for the localStorage quota, so — exactly like
// the announcement recordings — the blobs live in IndexedDB while the scheduler
// still reads the bell schedule itself synchronously from localStorage.

const IDB_NAME = "bell-sounds";
const IDB_VERSION = 1;
const IDB_STORE = "sounds";

// Fields on a bell/queue record that may carry the R2 audio URL.
const REMOTE_URL_FIELDS = ["bell", "url", "uri", "music", "fileUrl"];

// Return the first usable remote (http/https) bell URL from a bell record, or
// null when the bell has no backend file (so the caller can fall back locally).
export const getRemoteBellUrl = (bell) => {
    for (const field of REMOTE_URL_FIELDS) {
        const value = bell?.[field];
        if (typeof value === "string") {
            const trimmed = value.trim();
            if (/^https?:\/\//i.test(trimmed)) {
                return trimmed;
            }
        }
    }
    return null;
};

const collectRemoteUrls = (bells = []) => {
    const urls = new Set();
    (Array.isArray(bells) ? bells : []).forEach((bell) => {
        const url = getRemoteBellUrl(bell);
        if (url) {
            urls.add(url);
        }
    });
    return urls;
};

/* ------------------------------ IndexedDB ---------------------------- */

const openDb = () => {
    return new Promise((resolve, reject) => {
        if (typeof indexedDB === "undefined") {
            reject(new Error("IndexedDB is not available in this browser."));
            return;
        }

        const request = indexedDB.open(IDB_NAME, IDB_VERSION);

        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(IDB_STORE)) {
                db.createObjectStore(IDB_STORE, {keyPath: "url"});
            }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

const idbGet = async (url) => {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, "readonly");
        const request = tx.objectStore(IDB_STORE).get(url);
        request.onsuccess = () => {
            db.close();
            resolve(request.result || null);
        };
        request.onerror = () => {
            db.close();
            reject(request.error);
        };
    });
};

const idbPut = async (record) => {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, "readwrite");
        tx.objectStore(IDB_STORE).put(record);
        tx.oncomplete = () => {
            db.close();
            resolve();
        };
        tx.onerror = () => {
            db.close();
            reject(tx.error);
        };
    });
};

const idbGetAllKeys = async () => {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, "readonly");
        const request = tx.objectStore(IDB_STORE).getAllKeys();
        request.onsuccess = () => {
            db.close();
            resolve(request.result || []);
        };
        request.onerror = () => {
            db.close();
            reject(request.error);
        };
    });
};

const idbDelete = async (url) => {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, "readwrite");
        tx.objectStore(IDB_STORE).delete(url);
        tx.oncomplete = () => {
            db.close();
            resolve();
        };
        tx.onerror = () => {
            db.close();
            reject(tx.error);
        };
    });
};

/* ------------------------------- caching ----------------------------- */

// Download a single R2 file and store its blob.
//
// By default an already-cached URL is skipped (cheap logins). Pass
// {force: true} on a bell save/replace to re-download the file even when a blob
// already exists — this is what refreshes a bell whose audio was replaced at
// the SAME R2 URL (a brand-new URL is caught anyway, since it isn't cached).
// A plain GET (no custom headers) is used deliberately so the cross-origin
// request stays "simple" and never triggers a CORS preflight.
const cacheOne = async (url, {force = false} = {}) => {
    try {
        const existing = await idbGet(url);
        if (existing?.blob && !force) {
            return;
        }

        const response = await fetch(url, {cache: "no-store"});
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const blob = await response.blob();
        await idbPut({url, blob, mimeType: blob.type, cachedAt: Date.now()});
    } catch (e) {
        // A failed prefetch is non-fatal: at play time we simply fall back to
        // the live URL or the bundled local sound.
        console.warn("Unable to cache bell sound:", url, e);
    }
};

// Pre-fetch and cache every R2 bell file referenced by the given bells, and
// drop any previously cached file that is no longer in use. Safe to call
// fire-and-forget; it never rejects.
export const cacheBellSounds = async (bells = [], {force = false} = {}) => {
    try {
        const urls = collectRemoteUrls(bells);

        await Promise.all([...urls].map((url) => cacheOne(url, {force})));

        // Prune stale entries so the cache does not grow without bound.
        const existingKeys = await idbGetAllKeys();
        await Promise.all(
            existingKeys
                .filter((key) => !urls.has(key))
                .map((key) => idbDelete(key).catch(() => {}))
        );
    } catch (e) {
        console.warn("Bell sound caching failed:", e);
    }
};

// Return an object URL backed by the cached blob for a remote bell URL, or null
// when it has not been cached. The caller owns the object URL and must revoke
// it with URL.revokeObjectURL once playback finishes.
export const getCachedBellObjectUrl = async (remoteUrl) => {
    if (!remoteUrl) {
        return null;
    }
    try {
        const record = await idbGet(remoteUrl);
        if (record?.blob) {
            return URL.createObjectURL(record.blob);
        }
    } catch (e) {
        console.warn("Unable to read cached bell sound:", remoteUrl, e);
    }
    return null;
};
